import {
  AuthContext,
  type AuthContextValue,
  type SignInPayload,
} from '@entities/auth';
import { useClearStore } from '@entities/auth/hooks';
import { useSetState } from '@mantine/hooks';
import { ECookies } from '@shared/config';
import { setCookieSafely } from '@shared/config/cookies/helpers';

import React, { type PropsWithChildren, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useNavigate, useLocation } from 'react-router';
import { useShowMe } from '@entities/auth/api';
import { checkPublicRoutes } from './helpers';
import { AUTH_ROUTES } from './constants';

export const SessionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const clearStore = useClearStore();
  const navigate = useNavigate();
  const {
    data,
    isPending: isPendingUser,
    isFetching: isFetchingUser,
    isError,
    refetch,
  } = useShowMe();

  // Проверяем наличие необходимых токенов для авторизации
  const token = Cookies.get(ECookies.TOKEN);
  const tokenType = Cookies.get(ECookies.TOKEN_TYPE);

  const hasValidTokens = !!token && !!tokenType;

  const [authState, setAuthState] = useSetState<AuthContextValue>({
    isAuthenticated: false,
    user: null,
    signIn: () => undefined,
    signOut: () => undefined,
    isLoading: true,
  });
  const { pathname } = useLocation();

  useEffect(() => {
    if (!hasValidTokens && !checkPublicRoutes(pathname)) {
      clearStore();
      navigate('/auth');
    }
  }, [hasValidTokens, pathname]);

  useEffect(() => {
    // Если нет токенов, устанавливаем состояние как неавторизованное
    if (!hasValidTokens) {
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
      return;
    }

    // Если есть ошибка при получении данных пользователя
    if (isError) {
      clearStore();
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
      navigate('/auth');
      return;
    }

    // Если данные пользователя загружаются
    if (isPendingUser || isFetchingUser) {
      setAuthState(prev => ({
        ...prev,
        isLoading: true,
      }));
      return;
    }

    // Если данные пользователя получены успешно
    if (data) {
      const token = Cookies.get(ECookies.TOKEN);
      const tokenExpiresIn = Cookies.get(ECookies.TOKEN_EXPIRES_IN);

      setAuthState({
        isAuthenticated: true,
        user: {
          tokenExpiresIn: Number(tokenExpiresIn),
          token: String(token),
          ...data,
        },
        isLoading: false,
      });

      // Если пользователь на странице авторизации, перенаправляем на главную
      if (AUTH_ROUTES.includes(pathname)) {
        navigate('/');
      }
    }
  }, [hasValidTokens, isFetchingUser, isPendingUser, isError, data, pathname]);

  const value: AuthContextValue = React.useMemo(
    () => ({
      ...authState,
      signIn: (payload: SignInPayload) => {
        setCookieSafely(ECookies.TOKEN, payload.token);
        setCookieSafely(ECookies.TOKEN_TYPE, payload.tokenType);
        setCookieSafely(
          ECookies.TOKEN_EXPIRES_IN,
          payload.tokenExpiresIn.toString()
        );
        setCookieSafely(ECookies.REFRESH_TOKEN, payload.refreshToken);
        setAuthState({
          isAuthenticated: true,
          user: { ...payload },
        });
        refetch();
      },
      signOut: () => {
        navigate('/auth');

        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
        });

        clearStore();
      },
    }),
    [authState]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
